import { useState, FC } from "react"
import useCreateMenuSection from '../../hooks/MenuSection/useCreateMenuSection'
import MenuSectionEdit from "./MenuSectionEdit";
import ComponentCard from "../../components/common/ComponentCard";
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import { useToast } from "../../context/ToastContext";

import type { 
  MenuSection,
  MenuSectionListProps
} from "../../types/menuSectionTypes";

const SECTIONS_LIMIT = 5;

const MenuSectionList: FC<MenuSectionListProps> = ({ menu }) => {
  const toast = useToast();
  const { createMenuSection } = useCreateMenuSection({
    onCompleted: (menuSectionCreated) => {
      if (menuSectionCreated) {
        setMenuSections(prev => [...prev, menuSectionCreated as MenuSection]);
        setSectionName('')
        toast.success(`Section ${menuSectionCreated.name} created successfully`);
      }
    },
    onError: (err) => {
      if (err) return toast.error(String(err)); 
    }
  });

  const [sectionName, setSectionName] = useState<string>("")
  const [hintSectionInput, setHintSectionInput] = useState<string>("")
  const [errorSectionInput, setErrorSectionInput] = useState<boolean>(false)
  const [menuSections, setMenuSections] = useState<MenuSection[]>(menu.menuSections || [])

  const addSection = async () => {
    if (sectionName === '') {
      setErrorSectionInput(true)
      setHintSectionInput("Can't be empty string")
      return
    }
    await createMenuSection({
      name: sectionName,
      menuId: menu.id
    });
  }

  const updateSection = (section: MenuSection) => {
    const updatedSections = menuSections.map(ms => ms.id === section.id ? section : ms);
    setMenuSections(updatedSections)
  }

  const deleteSection = (section: MenuSection) => {
    const updatedSections = menuSections.filter(ms => ms.id !== section.id);
    setMenuSections(updatedSections)
  }

  const maxSectionsValidation = () => {
    return menuSections.length === SECTIONS_LIMIT
  }

  return (
    <ComponentCard title="Max 5 Sections">
      <div className="inline-flex w-full">
        <Input
          className="h-12"
          parentClassName="w-full"
          placeholder="Name for new section"
          value={sectionName}
          id={`menu.${menu.id}.sectionName`}
          hint={hintSectionInput}
          error={errorSectionInput}
          disabled={maxSectionsValidation()}
          onChange={(e) => {
            if (e.target.value !== '') {
              setErrorSectionInput(false)
              setHintSectionInput("")
            }
            setSectionName(e.target.value)
          }}  
        />
        <Button
          className="ml-5 h-12"
          onClick={addSection}
          disabled={maxSectionsValidation()}
        >
          Add
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-5">
        {menuSections.map((ms: MenuSection) => <MenuSectionEdit menuSection={ms} onUpdate={updateSection} onDelete={deleteSection} />)}
      </div>
    </ComponentCard>
  )
}

export default MenuSectionList;
